import React from 'react';
import { HostWithStats } from '../services/hostService.ts';
import { HostProfileActions } from './HostProfileActions.tsx';

interface HostDetailProps {
  host: HostWithStats;
  onClose: () => void;
}

export const HostDetail: React.FC<HostDetailProps> = ({ host, onClose }) => {
  const description = (host.short_description || host.description || '').trim();
  const coverUrl = (host.cover_Image || '').trim();
  const logoUrl = (host.logo_Image || '').trim();

  const formatGeo = () => {
    if (!host.latLong) return 'Missing';
    const ll = host.latLong as any;
    const lat = ll.latitude ?? ll._latitude;
    const lng = ll.longitude ?? ll._longitude;
    if (lat != null && lng != null) return `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
    return 'Invalid';
  };

  return (
    <div className="host-detail-panel">
      <div className="host-detail-header">
        <h3>{host.title || '(untitled)'}</h3>
        <button className="host-detail-close" onClick={onClose} title="Close">&times;</button>
      </div>

      <div className="host-detail-body">
        {/* Cover preview */}
        <div className="host-detail-cover-section">
          {coverUrl ? (
            <div className="host-detail-cover-wrapper">
              <img
                src={coverUrl.startsWith('//') ? `https:${coverUrl}` : coverUrl}
                alt="Cover"
                className="host-detail-cover-img"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              <span className={`host-detail-cover-badge ${host.coverStatus}`}>
                {host.coverStatus === 'ok' ? 'Raster' : host.coverStatus === 'svg' ? 'SVG' : 'Missing'}
              </span>
            </div>
          ) : (
            <div className="host-detail-cover-placeholder">No cover image</div>
          )}
          {logoUrl && (
            <div className="host-detail-logo-wrapper">
              <img
                src={logoUrl.startsWith('//') ? `https:${logoUrl}` : logoUrl}
                alt="Logo"
                className="host-detail-logo-img"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              <span className="host-detail-logo-label">Logo</span>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div className="host-detail-meta">
          <div className="host-detail-row">
            <span className="host-detail-label">Host ID</span>
            <code className="host-detail-value">{host.id}</code>
          </div>
          <div className="host-detail-row">
            <span className="host-detail-label">Type</span>
            <span className="host-detail-value">{host.host_type || '(unknown)'}</span>
          </div>
          <div className="host-detail-row">
            <span className="host-detail-label">Official URL</span>
            <span className="host-detail-value">
              {host.officialUrl ? (
                <a href={host.officialUrl} target="_blank" rel="noopener noreferrer">{host.officialUrl}</a>
              ) : (
                <span className="hosts-missing">(none)</span>
              )}
            </span>
          </div>
          <div className="host-detail-row">
            <span className="host-detail-label">Geo</span>
            <span className={`host-detail-value ${host.hasGeo ? '' : 'hosts-missing'}`}>{formatGeo()}</span>
          </div>
          <div className="host-detail-row">
            <span className="host-detail-label">Linked Posts</span>
            <span className="host-detail-value">{host.linkedPostCount}</span>
          </div>
          <div className="host-detail-row">
            <span className="host-detail-label">Aliases</span>
            <span className="host-detail-value">
              {(host.aliases || []).length > 0
                ? (host.aliases || []).join(', ')
                : <span className="hosts-missing">(none)</span>
              }
            </span>
          </div>
          <div className="host-detail-row">
            <span className="host-detail-label">User ID</span>
            <span className="host-detail-value">{host.userId || '-'}</span>
          </div>
          <div className="host-detail-row">
            <span className="host-detail-label">Profile Source</span>
            <span className="host-detail-value">{host.profile_source || '-'}</span>
          </div>
          <div className="host-detail-row">
            <span className="host-detail-label">Updated</span>
            <span className="host-detail-value">
              {host.updatedAt ? new Date(host.updatedAt).toLocaleString() : '-'}
            </span>
          </div>
          <div className="host-detail-row">
            <span className="host-detail-label">Created</span>
            <span className="host-detail-value">
              {host.createdAt ? new Date(host.createdAt).toLocaleString() : '-'}
            </span>
          </div>
        </div>

        {/* Description */}
        <div className="host-detail-description">
          <span className="host-detail-label">Description</span>
          {description ? (
            <p className="host-detail-desc-text">{description}</p>
          ) : (
            <p className="hosts-missing">(no description)</p>
          )}
        </div>

        {/* Issues */}
        {(!host.hasGeo || host.coverStatus !== 'ok' || !host.hasDescription || !host.officialUrl) && (
          <div className="host-detail-issues">
            <span className="host-detail-label">Issues</span>
            <ul className="host-detail-issues-list">
              {!host.hasGeo && <li>Missing latLong coordinates</li>}
              {host.coverStatus === 'missing' && <li>No cover image URL</li>}
              {host.coverStatus === 'svg' && <li>Cover is SVG (may not render in app)</li>}
              {!host.hasDescription && <li>Description is missing or too short (&lt;40 chars)</li>}
              {!host.officialUrl && <li>No official website URL</li>}
            </ul>
          </div>
        )}

        <HostProfileActions hostId={host.id} hostTitle={host.title} />
      </div>
    </div>
  );
};
